import { useState } from 'react';
import { Camera, ScanBarcode } from 'lucide-react';
import { normalizeBarcode, useTranslation } from '@dukanti/shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

interface BarcodeSearchCompactProps {
  readonly surface?: 'products' | 'stock';
  readonly pending?: boolean;
  readonly disabled?: boolean;
  readonly onSubmit?: (rawBarcode: string) => void;
  readonly onSearch?: (rawBarcode: string) => void;
  readonly onScan?: () => void;
}

export function BarcodeSearchCompact({
  surface,
  pending = false,
  disabled = false,
  onSubmit,
  onSearch,
  onScan,
}: BarcodeSearchCompactProps): React.JSX.Element {
  const { t } = useTranslation();
  const [value, setValue] = useState('');

  const submit = () => {
    if (normalizeBarcode(value).length === 0) return;
    (onSubmit ?? onSearch)?.(value);
  };

  return (
    <div
      className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row"
      data-testid={surface ? `${surface}-barcode-search-compact` : 'barcode-search-compact'}
    >
      <Input
        value={value}
        onChange={(event) => setValue(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            submit();
          }
        }}
        disabled={disabled || pending}
        dir="ltr"
        autoComplete="off"
        autoCapitalize="off"
        placeholder={t('admin.barcode.scan.inputPlaceholder')}
        aria-label={t('admin.barcode.entry.searchByBarcode')}
        className="text-start"
        data-testid={surface ? `${surface}-barcode-search-input` : 'barcode-search-input'}
      />
      <Button
        type="button"
        variant="outline"
        onClick={submit}
        disabled={disabled || pending || value.trim().length === 0}
        data-testid={surface ? `${surface}-barcode-search-submit` : 'barcode-search-submit'}
      >
        <ScanBarcode className="me-2 size-4" aria-hidden />
        {t('admin.barcode.entry.searchByBarcode')}
      </Button>
      {onScan ? (
        <Button
          type="button"
          variant="outline"
          onClick={onScan}
          disabled={disabled || pending}
          data-testid={surface ? `${surface}-barcode-scan-open` : 'barcode-scan-open'}
        >
          <Camera className="me-2 size-4" aria-hidden />
          {t('admin.barcode.entry.scanBarcode')}
        </Button>
      ) : null}
    </div>
  );
}
